<!-- TRANSMITTING — the theatre, and the one place the app tells the truth.
     SKIP is a persistent full-width control, first in the focus order, with no
     timeout. Not a small X and not a timed window: a switch-control user
     cannot chase a moving target, and a parent with a screaming toddler taps
     three times and hopes.

     The progress bar's FIRST THIRD IS HONEST — it is driven by real dither
     scanline progress — and the rest is theatre. The chassis forbids fake
     percentages ("if the work cannot report progress, the state is busy"), and
     this is how the joke and that rule both survive.

     THE LCD IS THE DIFFERENCE BETWEEN A MACHINE AND A PROGRESS BAR. It used to
     read DIALLING… for the whole three seconds and then TRANSMISSION COMPLETE,
     which is a spinner with a serif. It now steps — DIALLING, CARRIER
     DETECTED, HANDSHAKE, SENDING PAGE 1 OF 1, CONFIRMING — off the same
     percentage the gauge is drawing, so the words and the bar can never
     disagree.

     The share sheet does not open until the page has finished crawling out.
     Two things depend on it: the payoff shot is not stepped on by a system
     modal, and iOS requires the blob to already exist when the gesture fires. -->
<template>
  <IonPage>
    <AppHeader title="Transmitting" :back="backTo" />
    <IonContent :fullscreen="true">
      <div class="pad stack" style="margin-top: var(--sp-4)">
        <!-- Only while there is something to skip. It stayed up after the
             transmission finished, doing nothing, which is exactly the
             question it prompted. -->
        <IonButton v-if="!done" expand="block" fill="outline" @click="skip">
          {{ say('SKIP THE NOISE', 'Skip the noise') }}
        </IonButton>

        <div class="panel">
          <div class="lcd" role="status" aria-live="polite">{{ line }}</div>
          <span class="lamp fault" :class="{ 'is-on': busy }">
            <i aria-hidden="true" /><span class="silkscreen">Send</span>
          </span>
        </div>

        <!-- A gauge, not a `<div style="height:10px">`. The width is the only
             thing that moves and it moves through one CSS transition. -->
        <div
          v-if="!done"
          class="gauge"
          role="progressbar"
          aria-label="Transmission"
          :aria-valuenow="pct"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <i :style="{ width: pct + '%' }" />
        </div>

        <!-- The same switch as SETUP. It was a native checkbox painted red,
             which between a gauge and a paper slot read as part of the machine
             drawing, not a control. Gone once the page is through: it and the
             gauge have nothing left to say, and on a small phone they were
             what pushed OPEN THE OUT TRAY below the fold. -->
        <IonToggle v-if="!done" class="speaker" :checked="m.speaker" @ionChange="toggleSpeaker">
          <span class="silkscreen">Speaker</span>
        </IonToggle>

        <!-- The slot, then the page coming out of it. The window is a fixed
             height from the first frame, so nothing below it ever moves. -->
        <div>
          <div class="slot" aria-hidden="true" />
          <!-- No settle on the skipped path: the paper is halfway out of the
               slot when SKIP is pressed, and an animation starting from its own
               keyframe would teleport it the rest of the way. Skipped, it
               finishes coming out fast (200ms) and stops. -->
          <div
            class="crawl"
            :class="{
              'is-out': out,
              'is-skipped': skipped,
              'is-done': done,
              'is-landed': done && !skipped,
            }"
          >
            <div class="sheet">
              <canvas ref="cv" :aria-label="alt" role="img" />
            </div>
          </div>
        </div>

        <template v-if="done">
          <IonButton
            expand="block" size="large" class="rise" data-motion
            :disabled="hand.busy.value" @click="hand.run()"
          >
            {{ hand.busy.value
              ? say('HANDING IT OVER…', 'Opening the share sheet…')
              : say('HAND IT OVER', 'Send the picture yourself') }}
          </IonButton>
          <IonButton
            expand="block" fill="outline"
            :disabled="pictureOnly.busy.value || hand.busy.value" @click="pictureOnly.run()"
          >
            {{ pictureOnly.busy.value
              ? say('HANDING IT OVER…', 'Opening the share sheet…')
              : say('PICTURE ONLY', 'Send just the picture') }}
          </IonButton>
          <p class="silkscreen says" style="margin: 0">{{ say('PICTURE ONLY IS FOR SLACK AND ITS KIND, WHICH POST THE LINK INSTEAD OF THE PAGE. THE REPLY LINK IS PRINTED ON IT.', 'Use Picture only for Slack and apps like it, which post the link instead of the page. The reply link is printed on the page.') }}</p>
          <IonButton
            v-if="canCopy" expand="block" fill="outline"
            :disabled="copy.busy.value" @click="copy.run()"
          >
            {{ copy.busy.value
              ? say('COPYING…', 'Copying…')
              : say('COPY IMAGE', 'Copy the picture') }}
          </IonButton>
          <p v-if="hand.error.value || pictureOnly.error.value" class="field-error">{{ hand.error.value || pictureOnly.error.value }}</p>
          <p class="silkscreen says center" role="status">{{ outcome }}</p>

          <!-- THE SAD PATH HAD NO EXIT. Dismiss the share sheet and this screen
               says "NOT SENT — SAVED TO OUTBOX." and then offers no way to the
               outbox: the only control left is a back chevron to the home
               screen. A finished screen that names a place has to be able to
               take you there, and a terminal screen needs two ways out — the
               one that finishes and the one that goes on. -->
          <IonButton expand="block" fill="clear" @click="toOutbox">
            {{ say('OPEN THE OUT TRAY', 'Open the out tray') }}
          </IonButton>
        </template>
      </div>
      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { framesFromGif } from '../fax/frames.js';
import { renderAnimated, bytesFromDataUrl, bitmapsFromClip } from '../fax/animate.js';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { IonPage, IonContent, IonButton, IonToggle, onIonViewDidEnter } from '@ionic/vue';
import { useRoute, useRouter } from 'vue-router';
import { AppHeader, useAction } from '../core/ui';
import { announce, prefersReducedMotion } from '../core/a11y.js';
import { haptic, toast } from '../core/util.js';
import { findFax, saveFax, storeReady } from '../store.js';
import {
  machine, ensureMachine, updateMachine, recordPage, pruneTray,
} from '../fax/machine.js';
import { renderPage, loadSource, thumbnail } from '../fax/render.js';
import { shareFax, toPng, copyImage } from '../fax/share.js';
import { isNative } from '../core/native.js';
import { replyFooter, shareText } from '../fax/reply.js';
import { say, plainEnglish, sentences } from '../fax/voice.js';

const route = useRoute();
const router = useRouter();
const cv = ref(null);
const m = machine;

const row = ref(null);
const pct = ref(0);
const out = ref(false);
const skipped = ref(false);
const done = ref(false);
const busy = ref(true);
const outcome = ref('');
let blob = null;
let loopFrames = [];   // preview frames of a moving fax, played once it has landed
let loopTimer = null;
let stopSound = () => {};
let timer = null;

// Kept in step with --dur-crawl in app.css: the CSS moves the paper, this
// moves the gauge, and they have to finish together.
const CRAWL_MS = 3200;

const canCopy = computed(() => !!navigator.clipboard?.write);

/** Back returns to whichever screen sent you here — see PreviewPage. */
// BACK IS BACK TO THE SHEET, with this page on it. It went to the machine,
// which threw away what had just been typed — the one screen a person would
// want to return to is the one they were editing.
const backTo = computed(() => (route.query.from === 'outbox'
  ? '/tabs/outbox'
  : `/tabs/machine/compose?fax=${encodeURIComponent(String(route.params.faxId))}`));

// `replace`, not `push`: re-entering a finished transmission from the back
// stack would re-run the whole render and print the page a second time.
const toOutbox = () => { haptic('tick'); router.replace('/tabs/outbox'); };
// THE ONLY THING A SCREEN READER GETS OFF THIS SCREEN. Assembled through
// `sentences()` so a subject or memo that already ends in a full stop does not
// come out as a doubled one, and so an empty field leaves no gap at all — the
// old template literal read "Fax to MUM. ." for a photo with no cover sheet.
const alt = computed(() => {
  const f = row.value;
  if (!f) return 'A fax';
  return sentences(`Fax to ${f.toName || 'nobody'}`, f.subject, f.memo);
});

/**
 * What the machine says it is doing, derived from the same number the gauge
 * is drawing. Nothing here is on a timer of its own, so the LCD cannot claim
 * to be sending a page while the bar is still in the honest third.
 */
const line = computed(() => {
  if (done.value) return say('TRANSMISSION COMPLETE', 'The page has printed.');
  const p = pct.value;
  if (plainEnglish.value) return 'Printing the page…';
  if (p < 12) return 'DIALLING…';
  if (p < 33) return 'CARRIER DETECTED';
  if (p < 45) return 'HANDSHAKE — 9600 BPS';
  if (p < 88) return `SENDING PAGE 1 OF ${row.value?.pages || 1}`;
  return 'CONFIRMING…';
});

/**
 * The speaker toggle has to reach the sound that is PLAYING, not only the
 * sound the next transmission will make. Turning it off mid-screech left the
 * screech running, which is the one failure this control exists to prevent —
 * somebody silences a phone in a quiet room and it keeps going.
 */
function toggleSpeaker() {
  const on = !m.value.speaker;
  updateMachine({ speaker: on });
  haptic('tick');
  if (!on) { stopSound(); stopSound = () => {}; return; }
  if (!done.value && !prefersReducedMotion?.()) stopSound = screechIfAllowed();
}

async function run() {
  const f = row.value;
  if (!f) return;

  let canvas;
  if (f.kind === 'gif' || f.kind === 'clip') {
    // A MOVING FAX. Every frame goes through the same page render — header,
    // cover band, artefacts and all — and the result ships as an animated GIF
    // rather than a PNG. The seed is held still across frames on purpose; see
    // animate.js.
    let frames;
    try {
      frames = f.kind === 'clip'
        ? await bitmapsFromClip(f.source)
        : await framesFromGif(bytesFromDataUrl(f.source));
    } catch {
      toast(say('THIS MACHINE CANNOT READ THAT GIF.', "This machine can't read that GIF."));
      finish(); return;
    }
    const out = await renderAnimated({
      bitmaps: frames.bitmaps, delaysMs: frames.delaysMs,
      fax: f, machine: m.value, replyUrl: replyFooter(f.fromName),
      onProgress: (p) => { pct.value = Math.round(p * 33); },
      previewFrames: 12, previewWidth: 540,
    });
    canvas = out.poster;
    blob = out.blob;
    // The page that comes out of the slot MOVES once it has landed. Small
    // frames, evenly sampled; the delays are stretched back to real time so
    // twelve frames play as long as the forty did.
    const stretch = frames.bitmaps.length / Math.max(1, out.previews.length);
    loopFrames = out.previews.map((pv) => ({ bitmap: pv.bitmap, delayMs: Math.max(60, Math.round(pv.delayMs * stretch)) }));
  } else {
    const source = await loadSource(f.source);

    // The honest third: real scanline progress from the dither.
    const r = await renderPage({
      source, fax: f, machine: m.value, replyUrl: replyFooter(f.fromName),
      onProgress: (p) => { pct.value = Math.round(p * 33); },
    });
    canvas = r.canvas;
  }
  if (!canvas) { finish(); return; }

  const el = cv.value;
  if (el) {
    el.width = canvas.width; el.height = canvas.height;
    const g = el.getContext('2d');
    g.imageSmoothingEnabled = false;
    g.drawImage(canvas, 0, 0);
  }
  if (f.kind !== 'gif' && f.kind !== 'clip') blob = await toPng(canvas);
  // The out tray on the machine shows the last page it printed. Cheap, small,
  // and the only thing that makes coming back a month later mean anything.
  if (row.value) row.value = saveFax({ ...row.value, thumb: thumbnail(canvas) });

  if (prefersReducedMotion?.()) {
    // Less movement, not none: one frame at the start position so the page
    // still visibly ARRIVES rather than having always been there.
    requestAnimationFrame(() => { out.value = true; finish(); });
    return;
  }
  if (m.value.speaker) stopSound = screechIfAllowed();

  // Theatre for the rest — the page crawling out. The paper is moved by one
  // CSS transform transition; this interval only advances the gauge.
  out.value = true;
  const started = Date.now();
  timer = setInterval(() => {
    const t = Math.min(1, (Date.now() - started) / CRAWL_MS);
    pct.value = 33 + Math.round(t * 67);
    if (t >= 1) finish();
  }, 60);
}

function screechIfAllowed() {
  // Imported lazily so a muted machine never even builds an AudioContext.
  let stop = () => {};
  import('../fax/sound.js').then(({ screech }) => { stop = screech(); }).catch(() => {});
  return () => stop();
}

/** SKIP THE NOISE. The paper snaps out rather than being left mid-slot. */
function skip() {
  if (done.value) return;
  haptic('tick');
  skipped.value = true;
  out.value = true;
  finish();
}

function startLoop() {
  stopLoop();
  const el = cv.value;
  if (!el || loopFrames.length < 2 || prefersReducedMotion?.()) return;
  const g = el.getContext('2d');
  let i = 0;
  const tick = () => {
    const f = loopFrames[i % loopFrames.length];
    i++;
    g.imageSmoothingEnabled = false;
    g.drawImage(f.bitmap, 0, 0, el.width, el.height);
    loopTimer = setTimeout(tick, f.delayMs);
  };
  tick();
}
function stopLoop() { clearTimeout(loopTimer); loopTimer = null; }

function finish() {
  if (done.value) return;
  clearInterval(timer);
  stopSound();
  pct.value = 100;
  out.value = true;
  busy.value = false;
  done.value = true;
  haptic('success');
  startLoop();
  // The fax is HELD until it is actually handed over. The outbox is
  // authoritative — a rendered fax exists whether or not it was shared.
  if (row.value) row.value = saveFax({ ...row.value, status: 'held' });
  recordPage();
  // The out tray holds fifty. Past that the oldest page falls out of it, which
  // is a thing an out tray does and is far better than the alternative: an
  // IndexedDB quota error on a write, which looks like a fax that printed
  // perfectly and is not there in the morning.
  const shed = pruneTray();
  if (shed) toast(say('PAPER TRAY FULL. THE OLDEST PAGE FELL OUT.', 'The out tray was full, so the oldest fax was removed.'));
  announce('Transmission complete. Nothing has been sent yet.');
  outcome.value = say(
    'HELD IN OUT TRAY. NOT YET COLLECTED.',
    'The page is in the out tray. Nothing has been sent yet.',
  );
  // THE SHARE SHEET OPENS ITSELF once the page has landed. On the phone the
  // native share plugin needs no tap; on the web a share without a gesture is
  // refused and falls through to a download nobody asked for, so there the
  // HAND IT OVER button stays the way in. A beat first, so the landed page is
  // seen before the system sheet slides over it.
  if (isNative && blob) setTimeout(() => { if (done.value && !hand.busy.value) hand.run(); }, 450);
}

// A share sheet takes as long as the OS takes, and the old handler was a bare
// async click: no busy state, no re-entry guard, so two taps opened two sheets
// on Android and the second one's result overwrote the first one's.
async function handOver(filesOnly) {
  if (!blob) throw new Error('NOTHING TO HAND OVER. THE PAGE DID NOT PRINT.');
  // The sender's name rides in the link, so tapping it opens a cover sheet
  // already addressed back to this machine. That is the loop.
  const res = await shareFax(blob, {
    text: shareText(row.value?.toName, row.value?.fromName),
    title: 'FAX',
    filesOnly,
  });
  if (res === 'shared') {
    row.value = saveFax({ ...row.value, status: 'sent', sentAt: new Date().toISOString() });
    outcome.value = say('SENT.', 'Handed over.');
    announce('Sent.');
    router.replace('/tabs/outbox');
  } else if (res === 'downloaded') {
    outcome.value = say(
      'SAVED TO THIS DEVICE. SEND IT YOURSELF.',
      'Saved to this device. Send it on yourself.',
    );
    announce('Saved to this device.');
  } else {
    // A dismissed share sheet is NOT a send, and saying so is the whole point.
    outcome.value = say(
      'NOT SENT — SAVED TO OUTBOX.',
      'Not sent. It is waiting in the out tray.',
    );
    announce('Not sent. Saved to the outbox.');
  }
}

const hand = useAction(() => handOver(false), {
  weight: 'pop',
  // An error has to say what to do next, and the reassuring half is the half
  // that matters here: nothing was lost, the page is still in the out tray.
  // Not wrapped in `say()` — `useAction` reads this once, at setup, before the
  // machine row has hydrated, so a reactive string here would always resolve to
  // the shouted one anyway.
  failed: 'THAT DID NOT GO. THE PAGE IS STILL IN THE OUT TRAY — TRY AGAIN.',
});
// PICTURE ONLY: the same page with no caption. For Slack and the apps like
// it that turn a caption with a link into a link preview and drop the file.
// The reply link is printed on the page, so the loop still closes.
const pictureOnly = useAction(() => handOver(true), { weight: 'pop' });

const copy = useAction(async () => {
  const ok = await copyImage(blob);
  outcome.value = ok
    ? say('COPIED. PASTE IT ANYWHERE.', 'Copied. You can paste it anywhere.')
    : say(
      'COULD NOT COPY. USE HAND IT OVER INSTEAD.',
      'That could not be copied. Use “Send the picture yourself” instead.',
    );
  announce(outcome.value);
}, { weight: 'tick' });

onMounted(async () => {
  await storeReady;
  await ensureMachine();
  row.value = findFax(route.params.faxId);
  await run();
});
// RE-ENTERED, NOT RE-MOUNTED. Back from here lands on the cover sheet, and
// SEND from there pushes this same route — which Ionic answers by showing the
// view it already has, finished, with the page as it was BEFORE the edit and
// the row still marked draft. A second visit is a second print.
let entered = false;
onIonViewDidEnter(async () => {
  if (!entered) { entered = true; return; }
  clearInterval(timer);
  stopSound(); stopSound = () => {};
  pct.value = 0; out.value = false; skipped.value = false; done.value = false;
  busy.value = true; outcome.value = ''; blob = null;
  stopLoop(); loopFrames = [];
  row.value = findFax(route.params.faxId);
  await run();
});
onBeforeUnmount(() => { clearInterval(timer); stopLoop(); stopSound(); });
</script>

<style scoped>
.speaker { min-height: var(--tap); --track-background-checked: var(--led-red); }
</style>
